import { Box, IconButton, Link, Text, Tooltip } from '@lego/klik-ui';
import { CheckTickBold, ControlClose, RedoSolid } from '@lego/klik-ui/icons';
import { Link as ReactRouterLink } from 'react-router-dom';
import { UserNameLink } from '@/components/common/UserNameLink';
import type { DeviceTransfer, DeviceTransferAction } from '@/hooks/device_transfer';
import { dateTimeFormat } from '@/utility/date';
import { paths } from '@/utility/global_paths';
import { createColumnHelper } from '../appTable';

const columnHelper = createColumnHelper<DeviceTransfer>();

const idColumn = columnHelper.accessor('id', {
  cell: (info) => (
    <Text fontFamily="mono" fontSize="sm">
      {info.getValue()}
    </Text>
  ),
  header: 'ID',
  meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  size: 80,
});

const deviceColumn = columnHelper.accessor('deviceId', {
  cell: (info) => (
    <Link as={ReactRouterLink} to={paths.deviceById(info.getValue())}>
      {info.getValue()}
    </Link>
  ),
  header: 'Device',
  id: 'device',
  meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  size: 100,
});

const fromWorkspaceColumn = columnHelper.accessor('fromWorkspaceId', {
  cell: (info) => (
    <Link as={ReactRouterLink} to={paths.workspaceById(info.getValue())}>
      {info.getValue()}
    </Link>
  ),
  header: 'From Workspace',
  id: 'fromWorkspace',
  meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  size: 140,
});

const toWorkspaceColumn = columnHelper.accessor('toWorkspaceId', {
  cell: (info) => (
    <Link as={ReactRouterLink} to={paths.workspaceById(info.getValue())}>
      {info.getValue()}
    </Link>
  ),
  header: 'To Workspace',
  id: 'toWorkspace',
  meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
  size: 140,
});

const statusColumn = columnHelper.accessor('status', {
  cell: (info) => info.getValue(),
  header: 'Status',
  meta: {
    filterVariant: {
      options: [
        { filterValue: 'PENDING', name: 'Pending' },
        { filterValue: 'APPROVED', name: 'Approved' },
        { filterValue: 'DENIED', name: 'Denied' },
        { filterValue: 'RETRACTED', name: 'Retracted' },
      ],
      type: 'singleSelect',
    },
  },
  size: 120,
});

const createdAtColumn = columnHelper.accessor('createdAt', {
  cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
  header: 'Created At',
  meta: { filterVariant: { type: 'dateRange' } },
});

const requesterColumn = columnHelper.accessor('requesterOid', {
  cell: (info) => <UserNameLink oid={info.getValue()} />,
  header: 'Requester',
  size: 160,
});

const responderColumn = columnHelper.accessor('responderOid', {
  cell: (info) => <UserNameLink oid={info.getValue()} />,
  header: 'Responder',
  size: 160,
});

const updatedAtColumn = columnHelper.accessor('updatedAt', {
  cell: (info) => new Date(info.getValue()).toLocaleString(dateTimeFormat.local),
  header: 'Updated At',
  meta: { filterVariant: { type: 'dateRange' } },
});

const makeActionsColumn = (
  onAction: (transferId: number, action: DeviceTransferAction) => void,
  actions: DeviceTransferAction[]
) =>
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const transfer = row.original;
      if (transfer.status !== 'PENDING') return null;
      return (
        <Box display="flex" gap={1}>
          {actions.includes('APPROVE') && (
            <Tooltip label="Approve">
              <IconButton
                aria-label="Approve"
                colorScheme="success"
                icon={<CheckTickBold />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onAction(transfer.id, 'APPROVE');
                }}
                size="sm"
                variant="outline"
              />
            </Tooltip>
          )}
          {actions.includes('DENY') && (
            <Tooltip label="Deny">
              <IconButton
                aria-label="Deny"
                colorScheme="error"
                icon={<ControlClose />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onAction(transfer.id, 'DENY');
                }}
                size="sm"
                variant="outline"
              />
            </Tooltip>
          )}
          {actions.includes('RETRACT') && (
            <Tooltip label="Retract">
              <IconButton
                aria-label="Retract"
                icon={<RedoSolid />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onAction(transfer.id, 'RETRACT');
                }}
                size="sm"
                variant="outline"
              />
            </Tooltip>
          )}
        </Box>
      );
    },
  });

/** Incoming transfers — hides 'To Workspace' (always current WS), actions: Approve + Deny */
export const incomingDeviceTransferTableColumns = (
  onAction?: (transferId: number, action: DeviceTransferAction) => void
) =>
  columnHelper.columns([
    idColumn,
    deviceColumn,
    fromWorkspaceColumn,
    statusColumn,
    requesterColumn,
    responderColumn,
    createdAtColumn,
    updatedAtColumn,
    ...(onAction ? [makeActionsColumn(onAction, ['APPROVE', 'DENY'])] : []),
  ]);

/** Outgoing transfers — hides 'From Workspace' (always current WS), actions: Retract */
export const outgoingDeviceTransferTableColumns = (
  onAction?: (transferId: number, action: DeviceTransferAction) => void
) =>
  columnHelper.columns([
    idColumn,
    deviceColumn,
    toWorkspaceColumn,
    statusColumn,
    requesterColumn,
    createdAtColumn,
    updatedAtColumn,
    ...(onAction ? [makeActionsColumn(onAction, ['RETRACT'])] : []),
  ]);
